import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { useOutletContext } from 'react-router-dom-v5-compat';

import CreateNewDirectoryWRModal from 'af-root/scenes/Company/Jobs/Preview/Attachments/CreateNewDirectoryWRModal';

import type AttachmentVM from 'ab-viewModels/attachment/attachment.viewModel';
import type WorkRequestDirectoryVM from 'ab-viewModels/directory/workRequestDirectory.viewModel';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { ALLOWED_FILE_TYPES_LIST } from '@acceligentllc/shared/enums/fileType';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import { fromFileType } from 'ab-enums/mimeType.enum';

import { loadingToast, successToast } from '@acceligentllc/storybook';

import ConfirmationModal from 'af-components/ConfirmationModal';
import DirectoriesAttachmentTree from 'af-components/DocumentsAttachmentsTree';
import ImageAttachmentPreviewModal from 'af-components/DocumentsAttachmentsTree/Modals/ImageAttachmentPreviewModal';
import type { EditAttachmentProps } from 'af-components/DocumentsAttachmentsTree/TreeTableBody/EditAttachmentModal';
import EditAttachmentModal from 'af-components/DocumentsAttachmentsTree/TreeTableBody/EditAttachmentModal';

import * as AttachmentActions from 'af-actions/attachment';
import * as WorkRequestActions from 'af-actions/job';

type AttachmentVMExtended = AttachmentVM & {
	directoryId?: number;
};

interface TypedColumn {
	header: string;
	id?: string;
	width?: number;
}

interface DirectoriesAttachmentsOutletContext {
	jobId: number;
	hideCopyToWO?: boolean;
	hideAttachedToWO?: boolean;
	isReadOnly?: boolean;
}
interface RowActionOptions {
	actionName: string;
	actionFunction: (params: unknown) => void;
	disabled?: boolean;
	show?: boolean;
}

interface RowActions {
	parentDirectory: RowActionOptions[];
	childDirectory: RowActionOptions[];
	attachmentLevel: RowActionOptions[];
}

type Props = ConnectedProps<typeof connector>;

const allowedFileTypes = [ALLOWED_FILE_TYPES_LIST.map(fromFileType)].join(', ');

const DirectoriesAttachments = React.memo((props: Props) => {
	const {
		getWorkRequestDirectoriesAttachments,
		renameAttachment,
		downloadAttachment,
		deleteAttachment,
		createDirectory,
		deleteDirectory,
		changeAttachmentParent,
		uploadAttachmentsToDirectory,
	} = props;
	const [directoriesAndAttachments, setDirectoriesAndAttachments] = React.useState<Nullable<WorkRequestDirectoryVM[]>>(null);
	const [fileToRename, setFileToRename] = React.useState<EditAttachmentProps>({ directoryId: null, attachmentId: null, attachmentName: '', attachmentType: null });
	const [directoryId, setDirectoryId] = React.useState<Nullable<number>>(null);
	const [editAttachmentNameModalOpen, setEditAttachmentNameModalOpen] = React.useState(false);
	const [deleteDirectoryModalOpen, setdeleteDirectoryModalOpen] = React.useState(false);
	const [parentDirectoryForCreate, setParentDirectoryForCreate] = React.useState<Nullable<WorkRequestDirectoryVM>>(null);
	const [uploadDirectoryId, setUploadDirectoryId] = React.useState<Nullable<number>>(null);
	const [showImageAttachmentPreviewModal, setShowImageAttachmentPreviewModal] = React.useState<boolean>(false);
	const [imageAttachmentSrc, setImageAttachmentSrc] = React.useState<Nullable<string>>(null);

	const fileInputRef = React.useRef<HTMLInputElement>(null);

	const { jobId, hideAttachedToWO, hideCopyToWO, isReadOnly = false } = useOutletContext<DirectoriesAttachmentsOutletContext>();

	const fetchData = React.useCallback(async () => {
		const fetchedData = await getWorkRequestDirectoriesAttachments(+jobId);
		setDirectoriesAndAttachments(fetchedData);
	}, [getWorkRequestDirectoriesAttachments, jobId]);

	React.useEffect(() => {
		fetchData();
	}, [fetchData]);

	const createNewDirectory = React.useCallback(async (parent: WorkRequestDirectoryVM, name: string) => {
		await createDirectory(jobId, parent.id, name);
		await fetchData();
	}, [createDirectory, fetchData, jobId]);

	const updateAttachmentName = async (_directoryId: number, id: number, name: string) => {
		await renameAttachment(_directoryId, id, name);
		await fetchData();
	};

	const callChangeName = async (original: AttachmentVMExtended) => {
		const { id, name, directoryId: _directoryId } = original;
		const dotIndex = original.name.lastIndexOf('.');
		const attachmentNameWithoutType = (dotIndex !== -1) ? name.substring(0, dotIndex) : name;
		setFileToRename({ directoryId: _directoryId ?? null, attachmentId: id, attachmentName: attachmentNameWithoutType, attachmentType: original.type });
		setEditAttachmentNameModalOpen(!editAttachmentNameModalOpen);
	};

	const callDownloadAttachment = (original: AttachmentVM) => {
		const dotIndex = original.name.lastIndexOf('.');
		const attachmentNameWithoutType = (dotIndex !== -1) ? original.name.substring(0, dotIndex) : original.name;
		const fileName = `${attachmentNameWithoutType}${original.type}`;
		downloadAttachment(original.id, fileName);
	};

	const removeAttachmentFromDirectory = async (original: AttachmentVMExtended) => {
		const { id, directoryId: attachmentDirectoryId } = original;
		if (attachmentDirectoryId) {
			await deleteAttachment(id, attachmentDirectoryId);
		}
		await fetchData();

		successToast(
			`${id}-attachmentDeletion`,
			{ title: 'Attachment successfully deleted', timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) }
		);
	};

	const deleteOneDirectory = async () => {
		if (directoryId) {
			await deleteDirectory(jobId, directoryId);
		}
		setdeleteDirectoryModalOpen(false);

		successToast(
			`${directoryId}-directoryDeletion`,
			{ title: 'Directory successfully deleted', timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) }
		);

		await fetchData();
	};

	const callDeleteDirectory = async (original: WorkRequestDirectoryVM) => {
		const { id } = original;

		setdeleteDirectoryModalOpen(true);
		setDirectoryId(id);
	};

	const closeCreateModal = React.useCallback(() => {
		setParentDirectoryForCreate(null);
	}, []);

	const openImageAttachmentModal = React.useCallback((_imageAttachmentSrc: string) => {
		setImageAttachmentSrc(_imageAttachmentSrc);
		setShowImageAttachmentPreviewModal(true);
	}, []);

	const closeImageAttachmentModal = React.useCallback(() => {
		setImageAttachmentSrc(null);
		setShowImageAttachmentPreviewModal(false);
	}, []);

	const handleAttachmentParentChange = React.useCallback(async (attachmentId: number, sourceDirectoryId: number, destinationDirectoryId: number) => {
		await changeAttachmentParent(attachmentId, sourceDirectoryId, destinationDirectoryId);
		fetchData();
	}, [changeAttachmentParent, fetchData]);

	const handleUploadPress = React.useCallback((original: WorkRequestDirectoryVM) => {
		setUploadDirectoryId(original.id);
		fileInputRef.current?.click();
	}, []);

	const uploadAttachments = React.useCallback(async (selectedDirectoryId: number, files: File[]) => {
		const startingTimestamp = TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG);
		loadingToast(files[0].name, { title: `Uploading ${files[0].name}`, timestamp: startingTimestamp });

		await uploadAttachmentsToDirectory(selectedDirectoryId, { requestFiles: files });

		successToast(files[0].name, { title: 'Files successfully uploaded.', timestamp: startingTimestamp });

		await fetchData();
	}, [fetchData, uploadAttachmentsToDirectory]);

	const handleAttachmentUpload = React.useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
		const { files } = event.target;
		if (!files || !uploadDirectoryId) {
			return;
		}
		const requestFiles: File[] = [];
		for (let i = 0; i < files.length; i += 1) {
			const file = files.item(i);
			if (file) {
				requestFiles.push(file);
			}
		}

		await uploadAttachments(uploadDirectoryId, requestFiles);
		setUploadDirectoryId(null);
	}, [uploadAttachments, uploadDirectoryId]);

	const rowActions: RowActions = {
		parentDirectory: [
			{
				actionName: 'New folder',
				actionFunction: setParentDirectoryForCreate,
				/**
				 * To grey out (disable) action
				 */
				disabled: isReadOnly,
			},
		],
		childDirectory: [
			{
				actionName: 'New folder',
				actionFunction: setParentDirectoryForCreate,
				disabled: isReadOnly,
				show: true,
			},
			{
				actionName: 'Upload file',
				actionFunction: handleUploadPress,
				disabled: isReadOnly,
				show: true,
			},
			{
				actionName: 'Delete',
				actionFunction: callDeleteDirectory,
				disabled: isReadOnly,
				show: true,
			},
		],
		attachmentLevel: [
			{
				actionName: 'Rename',
				actionFunction: callChangeName,
				show: true,
			},
			{
				actionName: 'Download',
				actionFunction: callDownloadAttachment,
				show: true,
			},
			{
				actionName: 'Delete',
				actionFunction: removeAttachmentFromDirectory,
				show: true,
			},
		],
	};

	const columns = React.useMemo<TypedColumn[]>(() => {
		const columnData = [
			{
				header: 'Category folder',
				id: 'categoryFolder',
				width: 6,
			},
			{
				header: 'Files',
				id: 'files',
				width: 1,
			},
			{
				header: 'Download',
				id: 'download',
				width: 1,
			},
		];
		if (!isReadOnly) {
			columnData.push({
				header: 'Upload',
				id: 'upload',
				width: 1,
			});
		}
		columnData.push({
			header: 'Preview',
			id: 'preview',
			width: 1,
		});
		if (!hideCopyToWO) {
			columnData.push({
				header: 'Copy to Work Orders',
				id: 'copyToWO',
				width: 2,
			});
		}
		if (!hideAttachedToWO) {
			columnData.push({
				header: 'Attached to WO',
				id: 'attachedToWO',
				width: 2,
			});
		}
		columnData.push({
			header: 'Last modified',
			id: 'lastModified',
			width: 2,
		});
		return columnData;
	}, [hideAttachedToWO, hideCopyToWO, isReadOnly]);

	return (
		<>
			<DirectoriesAttachmentTree
				columns={columns}
				dataTree={directoriesAndAttachments}
				fetchData={fetchData}
				handleAttachmentParentChange={handleAttachmentParentChange}
				id={jobId}
				isReadOnly={isReadOnly}
				onAttachmentPreviewModalOpen={openImageAttachmentModal}
				rowActions={rowActions}
			/>
			<input
				accept={allowedFileTypes}
				onChange={handleAttachmentUpload}
				ref={fileInputRef}
				style={{ display: 'none' }}
				type="file"
			/>
			{editAttachmentNameModalOpen &&
				<EditAttachmentModal
					attachmentData={fileToRename}
					setModalVisible={setEditAttachmentNameModalOpen}
					showModal={editAttachmentNameModalOpen}
					updateAttachment={updateAttachmentName.bind(this)}
				/>
			}
			<CreateNewDirectoryWRModal
				closeModal={closeCreateModal}
				createDirectory={createNewDirectory}
				parent={parentDirectoryForCreate}
			/>
			{deleteDirectoryModalOpen &&
				<ConfirmationModal
					body="You are about to permanently delete the Directory and all the files in it. Are you sure you want to continue?"
					closeModal={setdeleteDirectoryModalOpen.bind(this, false)}
					confirmAction={deleteOneDirectory.bind(this)}
					confirmText="Delete"
					modalStyle="danger"
					showModal={deleteDirectoryModalOpen}
					title={'Delete Directory'}
				/>
			}
			<ImageAttachmentPreviewModal
				attachmentSrc={imageAttachmentSrc}
				onCloseModal={closeImageAttachmentModal}
				showModal={showImageAttachmentPreviewModal}
			/>
		</>
	);
});

function mapDispatchToProps() {
	return {
		getWorkRequestDirectoriesAttachments: WorkRequestActions.getWorkRequestDirectoriesAttachments,
		renameAttachment: AttachmentActions.renameAttachment,
		downloadAttachment: AttachmentActions.downloadAttachment,
		deleteAttachment: AttachmentActions.deleteAttachment,
		changeAttachmentParent: AttachmentActions.changeAttachmentParent,
		uploadAttachmentsToDirectory: AttachmentActions.uploadAttachmentsToDirectory,
		createDirectory: WorkRequestActions.createDirectory,
		deleteDirectory: WorkRequestActions.deleteDirectory,
	};
}

const connector = connect(null, mapDispatchToProps());

export default connector(DirectoriesAttachments);
